import { computed, ref } from 'vue';
import { faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { adminTermsClient } from '~/services';
import type { Term, TermFormData } from '~/services/adminTermsClient.types';

const DEFAULT_CONSENT_TEXT = 'I have read and agree to the Terms and Conditions.';
const DEFAULT_CONSENT_BUTTON = 'Accept';

const defaultData = (): TermFormData => ({
    title: '',
    description: null,
    content: '',
    consent_text: DEFAULT_CONSENT_TEXT,
    consent_button: DEFAULT_CONSENT_BUTTON,
    effective_at: '',
});

const populateFromTerm = (term: Term | null): TermFormData =>
    term
        ? {
              title: term.title,
              description: term.description,
              content: term.content ?? '',
              consent_text: term.consent_text,
              consent_button: term.consent_button,
              effective_at: term.effective_at,
          }
        : defaultData();

export function useTermFormSubmission(teamId: number, term: Term | null) {
    const data = ref<TermFormData>(populateFromTerm(term));
    const backHref = `/kiosk/teams/${teamId}/terms`;
    const isEditing = term !== null;
    const createdTermId = ref<number | null>(null);

    const { submit, processing } = useFormSubmission({
        data,
        submit: async () => {
            if (term) {
                return adminTermsClient.updateTerm(teamId, term.id, data.value);
            }

            const response = await adminTermsClient.createTerm(teamId, data.value);
            createdTermId.value = response.data.id;

            return response;
        },
        reset: () => {
            if (!term) {
                data.value = defaultData();
            }
        },
        notification: () => ({
            message: isEditing ? 'Terms updated' : 'Terms created',
            ...(isEditing ? {} : { redirect: `${backHref}/${createdTermId.value}` }),
            action: {
                icon: faSquareArrowUpLeft,
                label: 'Back to Terms',
                href: backHref,
            },
        }),
        preserveScroll: false,
    });

    const canEdit = computed(() => !term || term.status === 'draft');
    const canPublish = computed(() => term !== null && term.status === 'draft');

    const publish = () => {
        if (term === null) {
            return Promise.resolve();
        }

        return adminTermsClient.publishTerm(teamId, term.id).then(() => {
            window.location.assign(backHref);
        });
    };

    return {
        data,
        submit,
        processing,
        canEdit,
        canPublish,
        publish,
        backHref,
        isEditing,
    };
}
